/**
 * Centralized query key factory for React Query
 * Provides hierarchical query keys for easy invalidation
 */

export const queryKeys = {
  // Assignments
  assignments: {
    all: ["assignments"],
    lists: () => [...queryKeys.assignments.all, "list"],
    list: (filters) => [...queryKeys.assignments.lists(), filters],
    details: () => [...queryKeys.assignments.all, "detail"],
    detail: (id) => [...queryKeys.assignments.details(), id],
    submissions: (assignmentId) => [
      ...queryKeys.assignments.all,
      "submissions",
      assignmentId,
    ],
    submission: (assignmentId, submissionId) => [
      ...queryKeys.assignments.submissions(assignmentId),
      submissionId,
    ],
    departmentAssignmentsOfHod: (params = {}) => [
      ...queryKeys.assignments.all,
      "department-assignments-of-hod",
      params,
    ],
  },

  // Materials
  materials: {
    all: ["materials"],
    lists: () => [...queryKeys.materials.all, "list"],
    list: (filters) => [...queryKeys.materials.lists(), filters],
    details: () => [...queryKeys.materials.all, "detail"],
    detail: (id) => [...queryKeys.materials.details(), id],
  },

  // Attendance
  attendance: {
    all: ["attendance"],
    lists: () => [...queryKeys.attendance.all, "list"],
    list: (filters) => [...queryKeys.attendance.lists(), filters],
    details: () => [...queryKeys.attendance.all, "detail"],
    detail: (id) => [...queryKeys.attendance.details(), id],
    student: (studentId, filters) => [
      ...queryKeys.attendance.all,
      "student",
      studentId,
      filters,
    ],
    class: (classId, date) => [
      ...queryKeys.attendance.all,
      "class",
      classId,
      date,
    ],
  },

  // Users
  users: {
    all: ["users"],
    lists: () => [...queryKeys.users.all, "list"],
    list: (filters) => [...queryKeys.users.lists(), filters],
    details: () => [...queryKeys.users.all, "detail"],
    detail: (id) => [...queryKeys.users.details(), id],
    students: (filters) => [...queryKeys.users.all, "students", filters],
    teachers: (filters) => [...queryKeys.users.all, "teachers", filters],
    admins: (filters) => [...queryKeys.users.all, "admins", filters],
  },

  // Courses
  courses: {
    all: ["courses"],
    lists: () => [...queryKeys.courses.all, "list"],
    list: (filters) => [...queryKeys.courses.lists(), filters],
    details: () => [...queryKeys.courses.all, "detail"],
    detail: (id) => [...queryKeys.courses.details(), id],
    byDepartment: (departmentId) => [
      ...queryKeys.courses.all,
      "department",
      departmentId,
    ],
  },

  // Departments
  departments: {
    all: ["departments"],
    lists: () => [...queryKeys.departments.all, "list"],
    list: (filters) => [...queryKeys.departments.lists(), filters],
    details: () => [...queryKeys.departments.all, "detail"],
    detail: (id) => [...queryKeys.departments.details(), id],
  },

  // Leaves
  leaves: {
    all: ["leaves"],
    lists: () => [...queryKeys.leaves.all, "list"],
    list: (filters) => [...queryKeys.leaves.lists(), filters],
    details: () => [...queryKeys.leaves.all, "detail"],
    detail: (id) => [...queryKeys.leaves.details(), id],
    management: (filters) => [...queryKeys.leaves.all, "management", filters],
    pending: (filters) => [...queryKeys.leaves.all, "pending", filters],
    myLeaves: (filters) => [...queryKeys.leaves.all, "my", filters],
  },

  // Exams
  exams: {
    all: ["exams"],
    lists: () => [...queryKeys.exams.all, "list"],
    list: (filters) => [...queryKeys.exams.lists(), filters],
    details: () => [...queryKeys.exams.all, "detail"],
    detail: (id) => [...queryKeys.exams.details(), id],
    results: (studentId) => [...queryKeys.exams.all, "results", studentId],
  },

  // Timetable
  timetable: {
    all: ["timetable"],
    lists: () => [...queryKeys.timetable.all, "list"],
    list: (filters) => [...queryKeys.timetable.lists(), filters],
    details: () => [...queryKeys.timetable.all, "detail"],
    detail: (id) => [...queryKeys.timetable.details(), id],
    student: (studentId) => [...queryKeys.timetable.all, "student", studentId],
    teacher: (teacherId) => [...queryKeys.timetable.all, "teacher", teacherId],
    class: (classId) => [...queryKeys.timetable.all, "class", classId],
    byDate: (date) => [...queryKeys.timetable.all, "date", date],
    analytics: (id) => [...queryKeys.timetable.all, "analytics", id],
  },

  // Notifications
  notifications: {
    all: ["notifications"],
    lists: () => [...queryKeys.notifications.all, "list"],
    list: (filters) => [...queryKeys.notifications.lists(), filters],
    unread: () => [...queryKeys.notifications.all, "unread"],
  },

  // Chat
  chat: {
    all: ["chat"],
    users: (search = "") => [...queryKeys.chat.all, "users", search],
    conversations: () => [...queryKeys.chat.all, "conversations"],
    messages: (conversationId) => [
      ...queryKeys.chat.all,
      "messages",
      conversationId,
    ],
    groups: () => [...queryKeys.chat.all, "groups"],
    groupMessages: (groupId) => [
      ...queryKeys.chat.all,
      "groupMessages",
      groupId,
    ],
  },

  // Institutes
  institutes: {
    all: ["institutes"],
    lists: () => [...queryKeys.institutes.all, "list"],
    list: (filters) => [...queryKeys.institutes.lists(), filters],
    details: () => [...queryKeys.institutes.all, "detail"],
    detail: (id) => [...queryKeys.institutes.details(), id],
  },

  // RBAC
  rbac: {
    all: ["rbac"],
    roles: () => [...queryKeys.rbac.all, "roles"],
    permissions: () => [...queryKeys.rbac.all, "permissions"],
    rolePermissions: (roleId) => [
      ...queryKeys.rbac.all,
      "rolePermissions",
      roleId,
    ],
  },

  // Import/Export
  import: {
    all: ["import"],
    templates: () => [...queryKeys.import.all, "templates"],
    history: () => [...queryKeys.import.all, "history"],
  },

  // Broadcast
  broadcast: {
    all: ["broadcast"],
    lists: () => [...queryKeys.broadcast.all, "list"],
    list: (filters) => [...queryKeys.broadcast.lists(), filters],
    groups: () => [...queryKeys.broadcast.all, "groups"],
  },

  // Schedule
  schedule: {
    all: ["schedule"],
    lists: () => [...queryKeys.schedule.all, "list"],
    list: (filters) => [...queryKeys.schedule.lists(), filters],
    changes: () => [...queryKeys.schedule.all, "changes"],
  },

  // Recent Activities
  activities: {
    all: ["activities"],
  },

  // Advanced Reports
  advancedReports: {
    all: ["advancedReports"],
    department: (filters) => [...queryKeys.advancedReports.all, "department", filters],
    class: (filters) => [...queryKeys.advancedReports.all, "class", filters],
    student: (studentId, filters) => [...queryKeys.advancedReports.all, "student", studentId, filters],
  },

  // Organization (department-info org details)
  organization: {
    all: ["organization"],
    details: () => [...queryKeys.organization.all, "details"],
  },

  // Department view (department-info details page)
  departmentView: {
    all: ["departmentView"],
    details: (departmentId) => [...queryKeys.departmentView.all, "details", departmentId ?? "default"],
    departments: (instituteId) => [...queryKeys.departmentView.all, "departments", instituteId ?? "all"],
  },

  // Profile
  profile: {
    all: ["profile"],
    current: () => [...queryKeys.profile.all, "current"],
    contactRequests: () => [...queryKeys.profile.all, "contactRequests"],
    staffRequests: (filters = {}) => [
      ...queryKeys.profile.all,
      "staffRequests",
      filters,
    ],
    studentRequests: (filters = {}) => [
      ...queryKeys.profile.all,
      "studentRequests",
      filters,
    ],
  },

  // Subjects
  subjects: {
    all: ["subjects"],
    lists: () => [...queryKeys.subjects.all, "list"],
    list: (filters) => [...queryKeys.subjects.lists(), filters],
    details: () => [...queryKeys.subjects.all, "detail"],
    detail: (id) => [...queryKeys.subjects.details(), id],
    byCourse: (courseId) => [...queryKeys.subjects.all, "course", courseId],
  },

  // Teacher-Subject
  teacherSubject: {
    all: ["teacher-subject"],
    lists: () => [...queryKeys.teacherSubject.all, "list"],
    list: (filters) => [...queryKeys.teacherSubject.lists(), filters],
    details: () => [...queryKeys.teacherSubject.all, "detail"],
    detail: (id) => [...queryKeys.teacherSubject.details(), id],
    byTeacher: (teacherId) => [
      ...queryKeys.teacherSubject.all,
      "teacher",
      teacherId,
    ],
    bySubject: (subjectId, classId = null) => [
      ...queryKeys.teacherSubject.all,
      "subject",
      subjectId,
      ...(classId ? ["class", classId] : []),
    ],
    availability: (teacherId) => [
      ...queryKeys.teacherSubject.all,
      "availability",
      teacherId,
    ],
  },

  // Class-Subject
  classSubject: {
    all: ["class-subject"],
    lists: () => [...queryKeys.classSubject.all, "list"],
    list: (filters) => [...queryKeys.classSubject.lists(), filters],
    details: () => [...queryKeys.classSubject.all, "detail"],
    detail: (id) => [...queryKeys.classSubject.details(), id],
    byClass: (classId) => [...queryKeys.classSubject.all, "class", classId],
    bySubject: (subjectId) => [
      ...queryKeys.classSubject.all,
      "subject",
      subjectId,
    ],
  },
};

